<template>
	<button class="lg:hidden" aria-label="menu" @click="isSideMenuOpened = true">
		<span class="sr-only">Open main menu</span>
		<Bars3Icon class="h-6 w-6" aria-hidden="true" />
	</button>
	<TransitionRoot :show="isSideMenuOpened" appear as="template">
		<Dialog as="div" class="lg:hidden" @close="close">
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="duration-300 ease-out"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div class="fixed inset-0 z-50 bg-gray-500 bg-opacity-60" />
			</TransitionChild>
			<TransitionChild
				as="template"
				enter="duration-300 ease-in-out"
				enter-from="-translate-x-full"
				enter-to="translate-x-0"
				leave="duration-300 ease-out"
				leave-from="translate-x-0"
				leave-to="-translate-x-full"
			>
				<DialogPanel
					class="fixed inset-y-0 z-50 left-0 w-full overflow-y-auto pt-6 sm:max-w-sm sm:ring-1 sm:ring-gray-dark bg-secondary"
				>
					<div class="relative h-fit pb-10">
						<div class="px-4 flex items-center justify-between">
							<div>
								<ChevronLeftIcon
									v-if="currentNode || currentChildNode"
									class="cursor-pointer h-6 w-6"
									aria-hidden="true"
									@click="goBack"
								/>
							</div>
							<div>
								<h4 class="font-heldane uppercase">Menu</h4>
							</div>
							<button
								type="button"
								class="-m-2.5 rounded-md p-2.5 outline-none"
								@click="close"
							>
								<span class="sr-only">Close menu</span>
								<XMarkIcon class="h-6 w-6" aria-hidden="true" />
							</button>
						</div>
						<div class="border-t border-gray-dark mt-6 flow-root relative">
							<div class="divide-y divide-gray-500/10">
								<div>
									<template v-if="!currentNode && !currentChildNode">
										<LayoutSideMenuRow
											v-for="node in menu"
											:key="node.id"
											:node="node"
											cms-leaf
											@open="currentNode = $event"
											@close="close"
										/>
									</template>
									<template v-else-if="currentNode && !currentChildNode">
										<NuxtLink
											:to="resolveHref(currentNode)"
											class="cursor-pointer justify-between items-center border-b px-4 block py-3"
											@click="close"
										>
											{{
												$t("menu.show_all_category", {
													category: currentNode.name
												})
											}}
										</NuxtLink>
										<LayoutSideMenuRow
											v-for="childNode in currentNode.children"
											:key="childNode.id"
											:node="childNode"
											@open="currentChildNode = $event"
											@close="close"
										/>
									</template>
									<template v-else-if="currentChildNode">
										<NuxtLink
											:to="resolveHref(currentChildNode)"
											class="cursor-pointer justify-between items-center border-b px-4 block py-3"
											@click="close"
										>
											{{
												$t("menu.show_all_category", {
													category: currentChildNode.name
												})
											}}
										</NuxtLink>
										<NuxtLink
											v-for="subChildNode in currentChildNode.children"
											:key="subChildNode.id"
											:to="resolveHref(subChildNode)"
											class="cursor-pointer justify-between items-center border-b px-4 block py-3"
											@click="close"
										>
											{{ subChildNode.name }}
										</NuxtLink>
									</template>
								</div>
							</div>
						</div>
						<div
							class="py-2.5 fixed w-full sm:max-w-sm bottom-0 left-0 flex gap-5 items-center"
						>
							<div class="flex flex-col w-full">
								<div class="border-t border-gray-dark p-4">
									<LayoutLanguageCurrency
										:cultures="culturesNorce"
										:currencies="currenciesNorce"
									/>
								</div>
							</div>
						</div>
					</div>
				</DialogPanel>
			</TransitionChild>
		</Dialog>
	</TransitionRoot>
</template>

<script setup lang="ts">
import {
	Dialog,
	DialogPanel,
	TransitionRoot,
	TransitionChild
} from "@headlessui/vue";
import {
	XMarkIcon,
	Bars3Icon,
	ChevronLeftIcon
} from "@heroicons/vue/24/outline";
import type { Currency } from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type { CulturesNorce } from "~/types/components/header";
import type {
	ResolvedMenuNode,
	TopNavbarLeft
} from "~/types/storyblok/storyblok";

interface Props {
	topNavbarLeft: TopNavbarLeft[] | undefined;
	culturesNorce: CulturesNorce[] | undefined;
	currenciesNorce: Currency[] | undefined;
	menu: ResolvedMenuNode[];
}

const { culturesNorce, currenciesNorce, menu } = defineProps<Props>();

const { resolveMenuHref: resolveHref } = useStoryblokLink();
const currentNode = ref<ResolvedMenuNode | null>(null);
const currentChildNode = ref<ResolvedMenuNode | null>(null);

const isSideMenuOpened = inject("isSideMenuOpened", ref(false));

const close = () => {
	isSideMenuOpened.value = false;
	currentNode.value = null;
	currentChildNode.value = null;
};

const goBack = () => {
	if (currentChildNode.value) {
		currentChildNode.value = null;
	} else if (currentNode.value) {
		currentNode.value = null;
	}
};
</script>
